<template>
  <WModal :show="show" :title="modalTitle" :subtitle="isEdit ? `รหัส ${form.rule_id}` : 'บอกระบบว่าคำตอบแบบไหนในแบบสอบถามถือเป็นปัญหา — การเชื่อมสวัสดิการทำแยกในหน้า “ปัญหา → สวัสดิการ”'" size="lg" @close="$emit('close')">
    <form id="rule-form" class="flex flex-col gap-4" novalidate @submit.prevent="save()">
      <!-- 1. what is the problem -->
      <section class="rule-step">
        <div class="rule-step-head">
          <span class="rule-step-no">1</span>
          <div class="flex-1 min-w-0">
            <div class="rule-step-title">{{ isProblem ? "ปัญหาคืออะไร" : "กฎนี้คืออะไร" }}</div>
            <div class="rule-step-sub">ชื่อ มิติ และความเร่งด่วน คือสิ่งที่เจ้าหน้าที่เห็นบนหน้าครัวเรือน</div>
          </div>
          <div class="seg flex-shrink-0" role="group" aria-label="ประเภท">
            <button type="button" :class="{ 'seg--on': isProblem }" title="แสดงในรายการ “ปัญหาที่ตรวจพบ” บนหน้าครัวเรือน" @click="setKind('problem')">ขึ้นเป็นปัญหา</button>
            <button type="button" :class="{ 'seg--on': !isProblem }" title="ไม่ขึ้นเป็นปัญหา ใช้แนะนำสวัสดิการให้ผู้ที่เข้าเงื่อนไข เช่น ผู้สูงอายุ 60 ปีขึ้นไป" @click="setKind('welfare')">แนะนำสวัสดิการอย่างเดียว</button>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <div>
            <label class="form-label" for="rule-name">{{ isProblem ? "ชื่อปัญหา" : "ชื่อกฎ / กลุ่มเป้าหมาย" }} <span class="text-theme-24">*</span></label>
            <input id="rule-name" v-model.trim="form.name_th" type="text" class="form-control" :placeholder="isProblem ? 'เช่น ที่อยู่อาศัยไม่มั่นคง วัสดุชั่วคราว' : 'เช่น ผู้สูงอายุ 60 ปีขึ้นไป'">
            <div v-if="issueFor('name_th')" class="field-error">{{ issueFor('name_th') }}</div>
          </div>

          <div>
            <label class="form-label">มิติของปัญหา (5 มิติ) <span v-if="isProblem" class="text-theme-24">*</span></label>
            <div class="flex flex-wrap gap-1.5">
              <button v-for="d in DIMENSIONS" :key="d" type="button" class="dim-btn" :style="form.dimension === d ? { ...DIMENSION_STYLE[d], borderColor: DIMENSION_STYLE[d].color } : {}" @click="form.dimension = form.dimension === d && !isProblem ? null : d">
                <CheckIcon v-if="form.dimension === d" class="w-3.5 h-3.5" />{{ d }}
              </button>
            </div>
            <div v-if="issueFor('dimension')" class="field-error">{{ issueFor('dimension') }}</div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="form-label">ใครเป็นผู้มีปัญหา</label>
              <div class="grid grid-cols-2 gap-2">
                <button v-for="o in LEVEL_OPTIONS" :key="o.value" type="button" class="opt-card" :class="{ 'opt-card--on': form.level === o.value }" @click="setLevel(o.value)">
                  <component :is="o.icon" class="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span><span class="block font-medium">{{ o.label }}</span><span class="block text-xs opacity-80">{{ o.help }}</span></span>
                </button>
              </div>
            </div>
            <div v-if="isProblem">
              <label class="form-label">ความเร่งด่วน</label>
              <div class="grid grid-cols-2 gap-2">
                <button v-for="(l, k) in SEVERITY_LABEL" :key="k" type="button" class="opt-card" :style="form.severity === k ? { ...SEVERITY_STYLE[k], borderColor: SEVERITY_COLOR[k] } : {}" @click="form.severity = k">
                  <component :is="k === 'urgent' ? 'AlertTriangleIcon' : 'EyeIcon'" class="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span><span class="block font-medium">{{ l }}</span><span class="block text-xs opacity-80">{{ k === 'urgent' ? 'แสดงเป็นลำดับแรก' : 'ติดตามต่อเนื่อง' }}</span></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. which answers cause it -->
      <section class="rule-step">
        <div class="rule-step-head">
          <span class="rule-step-no">2</span>
          <div class="flex-1 min-w-0">
            <div class="rule-step-title">เกิดจากคำตอบข้อไหนในแบบสอบถาม</div>
            <div class="rule-step-sub">เลือกคำถาม แล้วคลิกคำตอบที่ถือว่า{{ isProblem ? "เป็นปัญหา" : "เข้าเงื่อนไข" }}</div>
          </div>
          <div v-if="mode !== 'mixed' && simpleList.length > 1" class="seg flex-shrink-0" role="group" aria-label="วิธีนับเงื่อนไข">
            <button type="button" :class="{ 'seg--on': mode === 'all' }" @click="setMode('all')">ต้องตรงทุกข้อ</button>
            <button type="button" :class="{ 'seg--on': mode === 'any' }" @click="setMode('any')">ตรงข้อใดข้อหนึ่งก็พอ</button>
          </div>
        </div>
        <div v-if="issueFor('if')" class="field-error mb-2">{{ issueFor('if') }}</div>

        <!-- one list (most rules) -->
        <div v-if="mode !== 'mixed'" class="flex flex-col gap-2">
          <div v-for="(c, i) in simpleList" :key="c._key" class="flex flex-col gap-2">
            <div v-if="i > 0" class="cond-joiner">{{ mode === 'all' ? 'และ' : 'หรือ' }}</div>
            <ConditionRow :model-value="c" :index="i" :questions="questions" :allowed-scopes="allowedScopes" :error="condError(mode, i)" @update:modelValue="(v) => Object.assign(c, v)" @remove="simpleList.splice(i, 1)" />
          </div>
          <button type="button" class="add-cond" @click="addCond(mode)"><PlusIcon class="w-4 h-4" /> {{ simpleList.length ? "เพิ่มเงื่อนไขอีกข้อ" : "เพิ่มเงื่อนไขข้อแรก" }}</button>
        </div>

        <!-- AND group + OR group (a few rules need both) -->
        <div v-else class="flex flex-col gap-3">
          <div v-for="b in BUCKETS" :key="b.key" class="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div class="text-sm font-medium text-gray-700 mb-2">{{ b.title }}</div>
            <div class="flex flex-col gap-2">
              <div v-for="(c, i) in form.if[b.key]" :key="c._key" class="flex flex-col gap-2">
                <div v-if="i > 0" class="cond-joiner">{{ b.joiner }}</div>
                <ConditionRow :model-value="c" :index="i" :questions="questions" :allowed-scopes="allowedScopes" :error="condError(b.key, i)" :move-title="b.moveTitle" @update:modelValue="(v) => Object.assign(c, v)" @remove="form.if[b.key].splice(i, 1)" @move="moveCond(b.key, i)" />
              </div>
              <button type="button" class="add-cond" @click="addCond(b.key)"><PlusIcon class="w-4 h-4" /> เพิ่มเงื่อนไขในกลุ่มนี้</button>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2 mt-2 text-xs text-theme-37">
          <button v-if="mode !== 'mixed'" type="button" class="underline" @click="mode = 'mixed'">ต้องการทั้ง “ต้องตรงทุกข้อ” และ “ตรงข้อใดข้อหนึ่ง” ในกฎเดียว</button>
          <button v-else type="button" class="underline disabled:opacity-50 disabled:no-underline" :disabled="form.if.all.length > 0 && form.if.any.length > 0" :title="form.if.all.length && form.if.any.length ? 'ย้ายเงื่อนไขให้เหลือกลุ่มเดียวก่อน' : ''" @click="leaveMixed">กลับไปใช้เงื่อนไขกลุ่มเดียว</button>
        </div>

        <div v-if="sentence" class="rule-summary">
          <InfoIcon class="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>เมื่อ{{ form.level === 'household' ? 'ครัวเรือน' : 'สมาชิกคนใดคนหนึ่ง' }}มีคำตอบว่า <strong>{{ sentence }}</strong> → {{ isProblem ? `ขึ้นเป็นปัญหา “${form.name_th || '…'}”` : 'ระบบแนะนำสวัสดิการที่เชื่อมไว้กับกฎนี้' }}</span>
        </div>
      </section>

      <!-- welfare lives on the mapping board -->
      <div class="rule-welfare-note">
        <GiftIcon class="w-4 h-4 flex-shrink-0 mt-0.5" style="color:#1f8f5b" />
        <div class="min-w-0">
          <template v-if="linkedWelfare.length">เชื่อมสวัสดิการไว้ {{ linkedWelfare.length }} รายการ: <span class="text-gray-700">{{ linkedWelfare.slice(0, 3).map((w) => w.name).join(' · ') }}<template v-if="linkedWelfare.length > 3"> และอีก {{ linkedWelfare.length - 3 }} รายการ</template></span></template>
          <template v-else>ยังไม่ได้เชื่อมสวัสดิการ</template>
          <span class="block text-xs text-theme-37">สวัสดิการเปลี่ยนทุกปี จึงจัดการแยกที่หน้า “ปัญหา → สวัสดิการ” โดยไม่ต้องแก้เงื่อนไขนี้</span>
        </div>
      </div>

      <!-- 3. optional -->
      <section class="rule-step rule-step--quiet">
        <button type="button" class="rule-toggle" @click="showMore = !showMore">
          <ChevronDownIcon class="w-4 h-4 transform transition-transform" :class="{ 'rotate-180': showMore }" /> ตั้งค่าเพิ่มเติม
          <span class="font-normal text-theme-37">ไม่บังคับ — คำอธิบายเหตุผล รหัสกฎ การเปิด/ปิด</span>
        </button>
        <div v-show="showMore" class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div class="md:col-span-2 flex items-center gap-3">
            <input id="rule-enabled" v-model="form.enabled" type="checkbox" class="form-check-switch">
            <label for="rule-enabled" class="text-sm text-gray-700">เปิดใช้งานกฎนี้</label>
          </div>
          <div v-if="!isProblem || form.then.flag">
            <label class="form-label">ความแน่ใจของสิทธิ์</label>
            <select v-model="form.then.action" class="form-select">
              <option v-if="form.then.action === 'need_flag'" value="need_flag">{{ ACTION_LABEL.need_flag }}</option>
              <option value="should_screen">ต้องให้เจ้าหน้าที่คัดกรองเพิ่ม</option>
              <option value="eligible">เข้าเกณฑ์ค่อนข้างแน่</option>
            </select>
          </div>
          <div class="flex items-center gap-2" :class="{ 'md:pt-6': !isProblem || form.then.flag }">
            <input id="rule-flag" v-model="form.then.flag" type="checkbox" class="form-check-input" true-value="missing_entitlement" :false-value="null">
            <label for="rule-flag" class="text-sm text-gray-700">ควรได้รับสิทธิ์แต่ยังไม่ได้รับ <span class="text-xs text-theme-37">(นับเป็นปัญหา)</span></label>
          </div>
          <div class="md:col-span-2">
            <label class="form-label">เหตุผล / เกณฑ์ที่ใช้ <span class="text-xs text-theme-37">(แสดงใต้ “ดูเหตุผลและเกณฑ์” บนหน้าครัวเรือน)</span></label>
            <textarea v-model.trim="form.rationale_th" rows="3" class="form-control" placeholder="อธิบายว่าทำไมคำตอบนี้จึงถือเป็นปัญหา"></textarea>
          </div>
          <div class="md:col-span-2">
            <label class="form-label">ข้อความหลักฐานรวมของปัญหา <span class="text-xs text-theme-37">(เว้นว่างเพื่อใช้ข้อความของเงื่อนไขที่ตรง · ตัวแปร {income_pc_month} {threshold_month} {neet_count} {working_age_count})</span></label>
            <input v-model.trim="form.evidence_tmpl" type="text" class="form-control" placeholder="เช่น รายได้เฉลี่ย {income_pc_month} บาท/เดือน/คน (เกณฑ์ {threshold_month})">
          </div>
          <div>
            <label class="form-label">ความเชื่อมั่นของกฎ <span class="text-xs text-theme-37">(บันทึกภายใน)</span></label>
            <select v-model="form.confidence" class="form-select">
              <option v-for="(l, k) in CONFIDENCE_LABEL" :key="k" :value="k">{{ l }}</option>
            </select>
          </div>
          <div>
            <label class="form-label">ชุดแบบสอบถาม</label>
            <select v-model="form.group" class="form-select">
              <option value="">อัตโนมัติ (ตามคำถามที่ใช้)</option>
              <option v-for="g in groupsForLevel" :key="g" :value="g">{{ GROUP_LABEL[g] || g }}</option>
            </select>
          </div>
          <div v-if="!isEdit">
            <label class="form-label">รหัสกฎ <span class="text-xs text-theme-37">(เว้นว่างให้ระบบกำหนด)</span></label>
            <input v-model.trim="form.rule_id" type="text" class="form-control" placeholder="เช่น HOUSE-R027">
            <div v-if="issueFor('rule_id')" class="field-error">{{ issueFor('rule_id') }}</div>
          </div>
          <div>
            <label class="form-label">ข้อสมมติ <span class="text-xs text-theme-37">(บันทึกภายใน)</span></label>
            <textarea v-model.trim="form.assumptions_th" rows="2" class="form-control"></textarea>
          </div>
          <div>
            <label class="form-label">ข้อมูลที่ยังขาด <span class="text-xs text-theme-37">(บรรทัดละ 1 ข้อ)</span></label>
            <textarea v-model="dataGapsText" rows="2" class="form-control"></textarea>
          </div>
        </div>
      </section>

      <!-- test -->
      <section class="rule-step rule-step--quiet">
        <button type="button" class="rule-toggle" @click="showTest = !showTest">
          <ChevronDownIcon class="w-4 h-4 transform transition-transform" :class="{ 'rotate-180': showTest }" /> ลองกับครัวเรือนจริงก่อนบันทึก
          <span class="font-normal text-theme-37">ไม่บังคับ</span>
        </button>
        <div v-show="showTest" class="mt-3">
          <div class="flex flex-wrap items-center gap-2">
            <input v-model.trim="testRef" type="text" class="form-control flex-1 min-w-[16rem]" placeholder="วางลิงก์หน้าครัวเรือน หรือกรอก รหัสครัวเรือน/รหัสแบบสำรวจ">
            <button type="button" class="btn btn-outline-secondary" :disabled="testing || !testIds" @click="runTest"><PlayIcon class="w-4 h-4 mr-1" /> ทดสอบ</button>
          </div>
          <div v-if="testError" class="field-error mt-2">{{ testError }}</div>
          <div v-if="testResult" class="mt-3 text-sm">
            <template v-if="testResult.level === 'household'">
              <span class="chip" :style="verdictStyle(testResult.household.verdict)">{{ verdictLabel(testResult.household.verdict) }}</span>
              <span v-if="testResult.household.matched_desc_th.length" class="text-theme-37 ml-2">{{ testResult.household.matched_desc_th.join(' · ') }}</span>
            </template>
            <template v-else>
              <div v-if="!testResult.members.length" class="text-theme-37">ครัวเรือนนี้ไม่มีสมาชิก</div>
              <div v-for="m in testResult.members" :key="m.member_id" class="flex flex-wrap items-center gap-2 py-1 border-b border-gray-100 last:border-0">
                <span class="chip" :style="verdictStyle(m.verdict)">{{ verdictLabel(m.verdict) }}</span>
                <span class="text-gray-800">{{ m.name_th }}</span><span v-if="m.age != null" class="text-theme-37 text-xs">{{ m.age }} ปี</span>
                <span v-if="m.matched_desc_th.length" class="text-theme-37 text-xs">{{ m.matched_desc_th.join(' · ') }}</span>
              </div>
            </template>
            <div class="text-xs text-theme-37 mt-1">"ข้อมูลไม่พอ" หมายถึงคำถามที่ใช้ยังไม่มีคำตอบในแบบสำรวจนี้ หรือระบบยังไม่รู้วิธีอ่านคำตอบ</div>
          </div>
        </div>
      </section>
    </form>

    <template #footer>
      <div v-if="saveError" class="text-sm text-theme-24 mr-auto whitespace-pre-line text-left max-w-md">{{ saveError }}</div>
      <button type="button" class="btn btn-outline-secondary" :disabled="saving" @click="$emit('close')">ยกเลิก</button>
      <button type="button" class="btn btn-outline-secondary" :disabled="saving" title="บันทึก แล้วเปิดหน้า ปัญหา → สวัสดิการ ที่ปัญหานี้" @click="save(true)">บันทึกและไปเชื่อมสวัสดิการ <ArrowRightIcon class="w-4 h-4 ml-1" /></button>
      <button type="submit" form="rule-form" class="btn btn-primary bg-primary-btn border-primary-btn" :disabled="saving">
        <LoadingIcon v-if="saving" icon="oval" class="w-4 h-4 mr-1" /> บันทึก
      </button>
    </template>
  </WModal>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import WModal from "../components/WModal.vue";
import ConditionRow from "./ConditionRow.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { DIMENSIONS, DIMENSION_STYLE, GROUP_LABEL, GROUP_FOR_LEVEL, ACTION_LABEL, SEVERITY_LABEL, SEVERITY_STYLE, SEVERITY_COLOR, CONFIDENCE_LABEL, OPS_NO_VALUE, isProblemRule } from "../constants";
import { conditionSentence } from "../ruleText";
import { toastSuccess } from "../components/useToast";

const props = defineProps({
  show: { type: Boolean, default: false },
  rule: { type: Object, default: null },        // null = create; { ...rule, _duplicate: true } = copy
  questions: { type: Array, default: () => [] },
  defaults: { type: Object, default: null },    // { dimension, level } taken from the list filters when creating
});
const emit = defineEmits(["close", "saved"]);

const LEVEL_OPTIONS = [
  { value: "household", label: "ครัวเรือน", help: "ใช้คำตอบของทั้งครัวเรือน", icon: "HomeIcon" },
  { value: "member", label: "สมาชิกรายคน", help: "ประเมินสมาชิกทีละคน", icon: "UserIcon" },
];
const LEVEL_SCOPES = {
  household: ["household", "house", "derived"],
  member: ["household", "house", "derived", "member", "member_identity", "cwd", "neet"],
};
const BUCKETS = [
  { key: "all", title: "กลุ่มที่ 1 — ต้องตรงทุกข้อ", joiner: "และ", moveTitle: "ย้ายไปกลุ่ม “ตรงข้อใดข้อหนึ่ง”" },
  { key: "any", title: "กลุ่มที่ 2 — และต้องตรงอย่างน้อย 1 ข้อในกลุ่มนี้", joiner: "หรือ", moveTitle: "ย้ายไปกลุ่ม “ต้องตรงทุกข้อ”" },
];

let keySeq = 0;
const newCond = (c = {}) => ({ _key: ++keySeq, question: c.question || "", op: c.op || "eq", value: c.value === undefined ? "" : c.value, field: c.field, value_desc_th: c.value_desc_th || "" });
const blank = () => ({
  rule_id: "", name_th: "", level: "household", group: "", enabled: true,
  if: { all: [], any: [] },
  then: { welfare_ids: [], action: "need_flag", flag: null },
  severity: "monitor", dimension: null, confidence: "medium",
  rationale_th: "", evidence_tmpl: "", assumptions_th: "",
});
const form = reactive(blank());
const mode = ref("all");             // all | any = one list · mixed = AND group + OR group
const dataGapsText = ref("");
const showMore = ref(false);
const showTest = ref(false);
const saving = ref(false);
const saveError = ref("");
const issues = ref([]);
const testRef = ref("");
const testing = ref(false);
const testError = ref("");
const testResult = ref(null);

const isEdit = computed(() => !!(props.rule && props.rule.rule_id && !props.rule._duplicate));
const isProblem = computed(() => isProblemRule(form));
const modalTitle = computed(() => (isEdit.value ? "แก้ไข" : "เพิ่ม") + (isProblem.value ? "ปัญหา" : "กฎแนะนำสวัสดิการ"));
const groupsForLevel = computed(() => GROUP_FOR_LEVEL[form.level] || []);
const allowedScopes = computed(() => LEVEL_SCOPES[form.level]);
const simpleList = computed(() => form.if[mode.value === "any" ? "any" : "all"]);
const qByCode = computed(() => Object.fromEntries(props.questions.map((x) => [x.code, x])));
const linkedWelfare = computed(() => (props.rule && props.rule.welfare) || []);
const sentence = computed(() => {
  const done = (c) => c.question && (OPS_NO_VALUE.includes(c.op) || (Array.isArray(c.value) ? c.value.length : c.value !== "" && c.value != null));
  const rule = { if: { all: form.if.all.filter(done), any: form.if.any.filter(done) } };
  return conditionSentence(rule, qByCode.value);
});

const issueFor = (path) => (issues.value.find((i) => i.path === path) || {}).message || "";
/** the API reports if.all[0].question / .op / .value — show whichever hit this row */
const condError = (bucket, i) => issues.value.filter((x) => x.path.startsWith(`if.${bucket}[${i}]`)).map((x) => x.message).join(" · ");

watch(() => props.show, (v) => { if (v) reset(); });

function reset() {
  Object.assign(form, blank());
  issues.value = []; saveError.value = ""; testResult.value = null; testError.value = ""; testRef.value = "";
  showMore.value = false; showTest.value = false; dataGapsText.value = "";
  const r = props.rule;
  if (r) {
    form.rule_id = r._duplicate ? "" : r.rule_id || "";
    form.name_th = r._duplicate ? `${r.name_th || ""} (สำเนา)` : r.name_th || "";
    form.level = r.level || "household";
    form.group = r._duplicate ? "" : r.group || "";
    form.enabled = r.enabled !== false;
    form.if.all = (r.if?.all || []).map(newCond);
    form.if.any = (r.if?.any || []).map(newCond);
    // kept as loaded: the editor never changes the welfare list (the mapping board does)
    form.then.welfare_ids = [...(r.then?.welfare_ids || [])].map(String);
    form.then.action = r.then?.action || "need_flag";
    form.then.flag = r.then?.flag || null;
    form.severity = r.severity || (isProblemRule(r) ? "monitor" : null);
    form.dimension = r.dimension || null;
    form.confidence = r.confidence || "medium";
    form.rationale_th = r.rationale_th || "";
    form.evidence_tmpl = r.evidence_tmpl || "";
    form.assumptions_th = r.assumptions_th || "";
    dataGapsText.value = (r.data_gaps || []).join("\n");
  } else {
    if (props.defaults?.dimension) form.dimension = props.defaults.dimension;
    if (props.defaults?.level) form.level = props.defaults.level;
    if (props.defaults?.kind === "welfare") { form.then.action = "should_screen"; form.severity = null; }
    form.if.all = [newCond()];
  }
  mode.value = form.if.all.length && form.if.any.length ? "mixed" : form.if.any.length ? "any" : "all";
}

function setKind(kind) {
  if (kind === "problem" && !isProblem.value) { form.then.action = "need_flag"; form.severity = form.severity || "monitor"; }
  if (kind === "welfare" && isProblem.value) { form.then.action = form.then.action === "need_flag" ? "should_screen" : form.then.action; form.then.flag = null; form.severity = null; }
}
const setLevel = (k) => { form.level = k; if (form.group && !groupsForLevel.value.includes(form.group)) form.group = ""; };
const addCond = (bucket) => form.if[bucket].push(newCond());
/** one list: switching "all" <-> "any" carries the conditions over */
function setMode(next) {
  if (next === mode.value) return;
  const from = mode.value === "any" ? "any" : "all";
  form.if[next] = [...form.if[next], ...form.if[from]];
  form.if[from] = [];
  mode.value = next;
}
const moveCond = (bucket, i) => { const [c] = form.if[bucket].splice(i, 1); form.if[bucket === "all" ? "any" : "all"].push(c); };
const leaveMixed = () => { mode.value = form.if.any.length ? "any" : "all"; };

/** questionnaire the rule belongs to, from the questions it reads (drives the HOUSE- / MEMBER- / CWD- … id prefix) */
function autoGroup() {
  const kinds = [...form.if.all, ...form.if.any].map((c) => qByCode.value[c.question]).filter(Boolean);
  if (form.level === "household") return kinds.length && kinds.every((q) => q.kind === "derived") ? "household_derived" : "house";
  if (kinds.some((q) => q.questionnaire === "cwd")) return "cwd";
  if (kinds.some((q) => q.questionnaire === "neets")) return "neet";
  return "member";
}

function buildBody() {
  const cond = ({ _key, ...c }) => { void _key; return c; };
  const used = (c) => c.question || c.value !== "";          // untouched blank rows are dropped, half-filled ones are reported
  return {
    rule_id: form.rule_id || undefined, name_th: form.name_th, level: form.level, group: form.group || autoGroup(), enabled: form.enabled,
    if: { all: form.if.all.filter(used).map(cond), any: form.if.any.filter(used).map(cond) },
    then: { welfare_ids: form.then.welfare_ids, action: form.then.action, flag: form.then.flag || null },
    severity: isProblem.value ? form.severity : null, dimension: form.dimension, confidence: form.confidence,
    rationale_th: form.rationale_th, evidence_tmpl: form.evidence_tmpl || null, assumptions_th: form.assumptions_th || null,
    data_gaps: dataGapsText.value.split("\n").map((s) => s.trim()).filter(Boolean),
  };
}

/** the same checks the API makes, worded for the form, so mistakes show up next to the field */
function validate(body) {
  const out = [];
  if (!body.name_th) out.push({ path: "name_th", message: isProblem.value ? "ต้องระบุชื่อปัญหา" : "ต้องระบุชื่อกฎ" });
  if (isProblem.value && !body.dimension) out.push({ path: "dimension", message: "เลือกมิติของปัญหา 1 มิติ" });
  if (body.if.all.length + body.if.any.length === 0) out.push({ path: "if", message: "ต้องมีเงื่อนไขอย่างน้อย 1 ข้อ" });
  for (const bucket of ["all", "any"]) {
    form.if[bucket].filter((c) => c.question || c.value !== "").forEach((c, i) => {
      const q = qByCode.value[c.question];
      if (!c.question) out.push({ path: `if.${bucket}[${i}].question`, message: "ยังไม่ได้เลือกคำถาม" });
      else if (q && q.scope && !allowedScopes.value.includes(q.kind === "derived" ? "derived" : q.scope)) out.push({ path: `if.${bucket}[${i}].question`, message: "คำถามนี้เป็นของสมาชิกรายคน ใช้กับปัญหาระดับครัวเรือนไม่ได้" });
      else if (!OPS_NO_VALUE.includes(c.op) && (Array.isArray(c.value) ? !c.value.length : c.value === "" || c.value == null)) out.push({ path: `if.${bucket}[${i}].value`, message: q && q.choices && q.choices.length ? "ยังไม่ได้เลือกคำตอบ" : "ยังไม่ได้ระบุค่า" });
    });
  }
  return out;
}

async function save(goMapping = false) {
  saveError.value = "";
  // drop untouched blank rows first so row numbers in messages match what is on screen
  for (const bucket of ["all", "any"]) form.if[bucket] = form.if[bucket].filter((c) => c.question || c.value !== "");
  const body = buildBody();
  issues.value = validate(body);
  if (issues.value.length) {
    if (!form.if.all.length && !form.if.any.length) addCond(mode.value === "any" ? "any" : "all");   // keep a row to fill in
    saveError.value = "ยังกรอกไม่ครบ — ดูข้อความสีแดงในฟอร์ม";
    return;
  }
  saving.value = true;
  try {
    const res = isEdit.value ? await welfareApi.rules.update(props.rule.rule_id, body) : await welfareApi.rules.create(body);
    toastSuccess(isEdit.value ? "บันทึกแล้ว" : `เพิ่ม ${res.item.rule_id} แล้ว`);
    emit("saved", res.item, { goMapping, created: !isEdit.value });
  } catch (e) {
    const d = e.response && e.response.data;
    issues.value = (d && d.data && d.data.issues) || [];
    saveError.value = apiErrorMessage(e);
  } finally {
    saving.value = false;
  }
}

// ---- test against a real household ----
const testIds = computed(() => {
  const s = testRef.value;
  if (!s) return null;
  const m = s.match(/household\/(?:view\/|edit\/|verify\/)?([^/?#]+)\/([^/?#]+)/) || s.match(/^\s*([^/\s]+)[\/\s]+([^/\s]+)\s*$/);
  return m ? { houseId: m[1], surveyId: m[2] } : null;
});
async function runTest() {
  if (!testIds.value) return;
  testing.value = true; testError.value = ""; testResult.value = null;
  try {
    testResult.value = await welfareApi.rules.test({ ...testIds.value, rule: buildBody() });
  } catch (e) {
    testError.value = apiErrorMessage(e, "ทดสอบไม่สำเร็จ");
  } finally {
    testing.value = false;
  }
}
const verdictLabel = (v) => ({ matched: "ตรงเงื่อนไข", not_matched: "ไม่ตรงเงื่อนไข", unknown: "ข้อมูลไม่พอ" }[v] || v);
const verdictStyle = (v) => ({
  matched: { color: "#1f6b45", background: "#e6f5ec", border: "1px solid #b7e1c8" },
  not_matched: { color: "#728096", background: "#f1f5f8", border: "1px solid #dce1ea" },
  unknown: { color: "#B36B00", background: "#fdf3df", border: "1px solid #f0dcae" },
}[v] || {});
</script>

<style scoped>
.rule-step { border: 1px solid #e2e8f0; border-radius: 0.5rem; padding: 0.9rem 1rem; background: #fff; }
.rule-step--quiet { background: #f7f9fb; padding: 0.65rem 1rem; }
.rule-step-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; margin-bottom: 0.8rem; }
.rule-step-no { width: 1.6rem; height: 1.6rem; border-radius: 9999px; background: #415A77; color: #fff; font-size: 0.85rem; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rule-step-title { font-size: 0.95rem; font-weight: 600; color: #2d3748; line-height: 1.3; }
.rule-step-sub { font-size: 0.78rem; color: #728096; }
.rule-toggle { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; font-size: 0.875rem; font-weight: 600; color: #2d3748; text-align: left; }
.seg { display: inline-flex; border: 1px solid #cbd5e0; border-radius: 0.375rem; overflow: hidden; }
.seg button { padding: 0.35rem 0.7rem; font-size: 0.8rem; background: #fff; color: #4a5568; white-space: nowrap; }
.seg button + button { border-left: 1px solid #cbd5e0; }
.seg button:hover { background: #f7f9fb; }
.seg button.seg--on { background: #415A77; color: #fff; }
.dim-btn { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.4rem 0.85rem; border-radius: 9999px; border: 1px solid #dce1ea; background: #fff; color: #4a5568; font-size: 0.85rem; font-weight: 500; transition: border-color 0.12s ease; }
.dim-btn:hover { border-color: #415A77; }
.opt-card { display: flex; align-items: flex-start; gap: 0.5rem; text-align: left; padding: 0.55rem 0.7rem; border-radius: 0.5rem; border: 1px solid #dce1ea; background: #fff; color: #4a5568; font-size: 0.85rem; line-height: 1.3; transition: border-color 0.12s ease; }
.opt-card:hover { border-color: #415A77; }
.opt-card--on { border-color: #415A77; background: #eef1f6; color: #2C3F56; }
.cond-joiner { align-self: center; font-size: 0.72rem; font-weight: 600; color: #415A77; background: #eef1f6; border-radius: 9999px; padding: 0.1rem 0.7rem; }
.add-cond { display: flex; align-items: center; justify-content: center; gap: 0.35rem; width: 100%; padding: 0.55rem; border: 1px dashed #a0aec0; border-radius: 0.5rem; color: #415A77; font-size: 0.85rem; background: #fff; }
.add-cond:hover { background: #eef1f6; border-color: #415A77; }
.rule-summary { display: flex; gap: 0.5rem; margin-top: 0.75rem; padding: 0.6rem 0.8rem; border-radius: 0.5rem; background: #eef1f6; color: #2C3F56; font-size: 0.85rem; line-height: 1.5; }
.rule-welfare-note { display: flex; gap: 0.5rem; padding: 0.6rem 0.8rem; border-radius: 0.5rem; border: 1px dashed #b7e1c8; background: #f3faf6; color: #1f6b45; font-size: 0.85rem; }
</style>
